import React, { useState, useEffect } from 'react'
import {
  Route,
  Switch,
  Redirect,
  useLocation,
  matchPath
} from 'react-router-dom'
import { Layout } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { getAllNotifications } from '../reducers/notificationsReducer'
import { initializeAuth } from '../reducers/authReducer'
import useMobile from '../hooks/useMobile'
import SideNav from '../components/SideNav'
import PublicRoute from '../components/PublicRoute'
import PrivateRoute from '../components/PrivateRoute'
import AdminRoute from '../components/AdminRoute'

import Login from '../pages/Login'
import NotFoundPage from '../pages/NotFoundPage'
import Registeration from '../pages/Registeration'
import ForgetPassword from '../pages/ForgetPassword'
import OAuthCallback from '../pages/OAuthCallback'
import OAuthRoleSelection from '../pages/OAuthRoleSelection'
import Home from '../pages/Home'
import PublicCourses from '../pages/PublicCourses'
import PublicCareers from '../pages/PublicCareers'
import {
  DiplomasPage,
  CertificatesPage,
  SkillPathsPage,
  CorporateTrainingPage,
  CategoryPage,
  AboutPage,
  SuccessStoriesPage,
  ContactPage,
  CoursePreviewPage,
} from '../pages/PublicPages'
import { PrivacyPage, TermsPage } from '../pages/LegalPages'
import Courses from '../views/courses'
import Articles from '../views/articles'
import MyArticle from '../views/articles/myArticles/myArticles'
import MyBookMarks from '../views/articles/myBookMarks/myBookMarks'
import ArticlePage from '../views/ArticlePage'
import Profile from '../views/Profile'
import Exams from '../views/exams'
import { AssessmentCreation, Submissions } from '../views/exams'
import Lectures from '../views/lectures'
import Modules from '../views/modules'
import AssessmentTaking from '../views/assessmentTaking'
import DiscussionFeed from '../views/discussions'
import AnnouncementsFeed from '../views/announcements'
import Dashboard from '../views/dashboard'
import CourseCalendar from '../views/courseCalendar'
import GradeBook from '../views/gradeBook'
import NotFoundView from '../views/NotFoundView'
import Grader from '../views/assessmentGrading'

import 'antd/dist/antd.css'
import 'ant-design-pro/dist/ant-design-pro.css'
import S from './style'

import AppHeader from '../components/AppHeader'
import CourseParticipants from '../views/courseParticipants'
import Assignments from '../views/assignments'
import CourseSettings from '../views/courseSettings'
import LiveSessionsList from '../views/liveSessions'
import LiveSessionRoom from '../views/liveSessionRoom'
import Achievements from '../views/achievements'
import CourseRoute from '../components/CourseRoute'
import Archives from '../views/archives'
import FilePreview from '../views/filePreview'
import AdminApp from '../views/admin'
import CookieConsent from '../components/CookieConsent'


// public routes redirects to /app if authenticated
// private routes redirects to login if not authenticated
const App = () => {
  const dispatch = useDispatch()
  const isAuth = useSelector(state => state.auth.isAuth)

  useEffect(() => {
    if (!isAuth) {
      const consent = localStorage.getItem('WiseAcademy-cookie-consent')
      // Only attempt cookie-based auto-login if user hasn't rejected cookies
      if (consent !== 'rejected') {
        dispatch(initializeAuth())
      }
    }
  }, [dispatch, isAuth])

  return (
    <Switch>
      {/* canonical lowercase auth routes */}
      <PublicRoute path="/register" component={Registeration} />
      <PublicRoute path="/forgetpassword" component={ForgetPassword} />
      <PublicRoute path="/login" component={Login} />
      <PublicRoute path="/oauth/callback" component={OAuthCallback} />
      <PublicRoute path="/oauth/select-role" component={OAuthRoleSelection} />

      {/* backward compatibility for older links */}
      <Route exact path="/Register">
        <Redirect to="/register" />
      </Route>
      <Route exact path="/Login">
        <Redirect to="/login" />
      </Route>
      <Route exact path="/ForgetPassword">
        <Redirect to="/forgetpassword" />
      </Route>

      <PrivateRoute path="/app" component={AuthnticatedApp} />
      <AdminRoute path="/admin" component={AdminApp} />
      {/* landing page */}
      <Route exact path="/" component={Home} />
      <Route path="/explore-courses" component={PublicCourses} />
      <Route path="/careers" component={PublicCareers} />

      {/* public footer pages */}
      <Route path="/diplomas" component={DiplomasPage} />
      <Route path="/certificates" component={CertificatesPage} />
      <Route path="/skill-paths" component={SkillPathsPage} />
      <Route path="/corporate-training" component={CorporateTrainingPage} />
      <Route path="/category/:slug" component={CategoryPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/success-stories" component={SuccessStoriesPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/course/:id" component={CoursePreviewPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />

      <Route path="*" component={NotFoundPage} />
      <CookieConsent />
    </Switch>
  )
}

const AuthnticatedApp = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isMobile = useMobile()
  const { Content } = Layout

  const location = useLocation()

  const onCollapse = (c) => setCollapsed(c)

  const currentLocationIS = (pathToMatch) => {
    const match = matchPath(location.pathname, pathToMatch)
    return !!match
  }

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getAllNotifications())
  }, [dispatch])

  const isMeetingRoom = currentLocationIS('/app/course/:courseId/live/:sessionId')

  return (
    <Layout style={{ minHeight: '100vh', background: isMeetingRoom ? '#202124' : undefined }}>
      {!isMeetingRoom && (
        <SideNav
          collapsed={collapsed}
          onCollapse={onCollapse}
          isMobile={isMobile}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
      )}

      <Layout
        style={{
          marginLeft: isMeetingRoom ? 0 : (isMobile ? 0 : (collapsed ? 80 : 200)),
          transition: 'margin-left .2s',
          width: isMeetingRoom ? '100%' : (isMobile ? '100%' : undefined),
          background: isMeetingRoom ? '#202124' : undefined
        }}
      >
        {!isMeetingRoom && (
          <AppHeader
            courseNavigation={currentLocationIS('/app/course/:id')}
            isMobile={isMobile}
            onMenuClick={() => setMobileMenuOpen(true)}
          />
        )}

        <Content
          style={{
            padding: isMeetingRoom ? 0 : (isMobile ? '16px 12px' : '20px 32px'),
            height: isMeetingRoom ? '100vh' : '100%',
            minHeight: isMeetingRoom ? '100vh' : (isMobile ? 'calc(100vh - 56px - 48px)' : undefined),
            background: isMeetingRoom ? '#202124' : undefined
          }}
        >
          <Switch>
            {/* redirect to courses page for now */}
            <Route exact path="/app/">
              <Redirect to="/app/courses" />
            </Route>

            <Route path="/app/dashboard" component={Dashboard} />
            <Route path="/app/calendar" component={CourseCalendar} />

            <Route path="/app/courses" component={Courses} />
            <CourseRoute
              path="/app/course/:courseId/modules"
              component={Modules}
            />

            <CourseRoute
              path="/app/course/:courseId/assessment/:assessmentId/submissions"
              component={Submissions}
            />
            <CourseRoute
              path="/app/course/:courseId/assessment/:assessmentId/grade"
              component={Grader}
            />
            <CourseRoute
              path="/app/course/:courseId/exams/create"
              render={(props) => (
                <AssessmentCreation {...props} assessmentType="Exam" />
              )}
            />
            <CourseRoute
              path="/app/course/:courseId/assignments/create"
              render={(props) => (
                <AssessmentCreation {...props} assessmentType="Assignment" />
              )}
            />
            <CourseRoute path="/app/course/:courseId/exams" component={Exams} />
            <CourseRoute
              path="/app/course/:courseId/assignments"
              component={Assignments}
            />
            <CourseRoute
              path="/app/course/:courseId/lectures/:lectureId"
              component={Lectures}
            />
            <CourseRoute
              path="/app/course/:courseId/lectures"
              component={Lectures}
            />

            <CourseRoute
              path="/app/course/:courseId/exam/:assessmentId"
              render={(props) => (
                <AssessmentTaking {...props} assessmentType={'Exam'} />
              )}
            />
            <CourseRoute
              path="/app/course/:courseId/assignment/:assessmentId"
              render={(props) => (
                <AssessmentTaking {...props} assessmentType={'Assignment'} />
              )}
            />

            <CourseRoute
              path="/app/course/:courseId/discussions"
              render={(props) => (
                <DiscussionFeed
                  {...props}
                  courseId={props.match.params.courseId}
                />
              )}
            />
            <CourseRoute
              path="/app/course/:courseId/gradebook"
              render={(props) => (
                <GradeBook {...props} courseId={props.match.params.courseId} />
              )}
            />
            <CourseRoute
              path="/app/course/:courseId/announcments"
              render={(props) => (
                <AnnouncementsFeed
                  {...props}
                  courseId={props.match.params.courseId}
                />
              )}
            />
            <CourseRoute
              path="/app/course/:courseId/particpants"
              component={CourseParticipants}
            />

            <CourseRoute
              path="/app/course/:courseId/settings"
              component={CourseSettings}
            />
            <CourseRoute
              path="/app/course/:courseId/live/:sessionId"
              component={LiveSessionRoom}
            />
            <CourseRoute
              path="/app/course/:courseId/live"
              component={LiveSessionsList}
            />

            <Route path="/app/articles/:id" component={ArticlePage} />
            <Route path="/app/articles" component={Articles} />
            <Route path="/app/myarticle" component={MyArticle} />
            <Route path="/app/myBookMarks" component={MyBookMarks} />
            <Route path="/app/profile" component={Profile} />
            <Route path="/app/archives" component={Archives} />
            <Route path="/app/acheivements" component={Achievements} />
            <Route path="/app/file-preview" component={FilePreview} />

            <Route path="/app/*" component={NotFoundView} />
          </Switch>
        </Content>
        {!isMeetingRoom && <S.Footer>Copyright ©2021 WiseAcademy</S.Footer>}
      </Layout>
    </Layout>
  )
}

export default App
